import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { endpoints } from 'apps/portal/online/services/core/apiConfig';
import { HomeRestaurant } from '../../(routes)/online-ordering/types';
import { getVisibilityParams, removeEmptyKeys } from 'apps/portal/online/utils/UtilFunctions';
import { BusinessApiResponse, TransformedBusinessData } from './restaurantSliceTypes';

// Define a service using a base URL and expected endpoints
// You can import or define the base URL like this:
const baseUrl = process.env.NEXT_PUBLIC_BASE_URL as string;

export const restaurantApi = createApi({
  reducerPath: 'restaurantApi',
  baseQuery: fetchBaseQuery({ baseUrl: `${baseUrl}` }),
  endpoints: (build) => ({
    getRestaurantData: build.query<
      HomeRestaurant,
      { restaurantId: number; userId: string; menuId?: number; orderType?: string }
    >({
      query: ({ restaurantId, userId, menuId, orderType }) => {
        const visibilityParams = getVisibilityParams();
        const payload: Record<string, any> = {
          userId,
          orderType,
          ...visibilityParams,
        };
        if (Number.isInteger(menuId)) {
          payload.menuId = JSON.stringify(menuId);
        }

        const params = removeEmptyKeys(payload);
        return {
          url: `${endpoints.restaurant.getOnlineRestaurant}/${restaurantId}`,
          method: 'GET',
          params,
        };
      },
      transformResponse: (response: any) => response?.data,
      keepUnusedDataFor: 15, // Reduce cache time to 15 seconds for status updates
    }),
    getBusinessByAlias: build.query<TransformedBusinessData, string>({
      query: (aliasName) => `${endpoints.business.getByAlias}/${aliasName}`,
      transformResponse: (response: BusinessApiResponse) => {
        const data = response.data;
        return {
          ...data,
          restaurant: data.restaurants?.[0] ?? null,
          restaurants: undefined,
        };
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetRestaurantDataQuery, useGetBusinessByAliasQuery } = restaurantApi;
